// Shared helpers for the cashier-cheque groups (Government / Seller / Agent / Bank / custom)
// used by CashierChequeCard.vue, ChequeGroup.vue, ClosingStatementPdf.vue, and PropertyTax.vue.

export function modeForKind(kind) {
  if (kind === 'government') return 'remainder-cash'
  if (kind === 'seller' || kind === 'agent') return 'remainder-last-cheque'
  return 'freeform'
}

export function targetLabelForKind(kind) {
  if (kind === 'government') return 'Tax & Fee Total'
  if (kind === 'seller') return 'Net to the Seller'
  if (kind === 'agent') return 'Commission Total'
  return ''
}

function blankCheque() {
  return { name: '', remark: '', amount: null }
}

export function createDefaultChequeGroups() {
  return [
    {
      id: 'government',
      kind: 'government',
      title: 'Government (Tax Payment)',
      manualLast: false,
      cheques: [{ name: '', remark: 'Ministry of Finance', amount: null }],
    },
    {
      id: 'seller', kind: 'seller', title: 'Seller', manualLast: false, cheques: [blankCheque()],
    },
    {
      id: 'agent', kind: 'agent', title: 'Agent (Commission)', manualLast: false, cheques: [blankCheque()],
    },
    {
      id: 'bank', kind: 'bank', title: 'Bank Loan Payoff', manualLast: false, cheques: [],
    },
  ]
}

export function createCustomChequeGroup() {
  return {
    id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    kind: 'custom',
    title: '',
    manualLast: false,
    cheques: [blankCheque()],
  }
}

export function chequeRemainder(group, target) {
  const lastIndex = group.cheques.length - 1
  if (lastIndex < 0) return 0
  const othersTotal = group.cheques.reduce(
    (sum, c, i) => (i === lastIndex ? sum : sum + Number(c.amount || 0)), 0,
  )
  return target - othersTotal
}

export function isLastLocked(group, index) {
  return modeForKind(group.kind) === 'remainder-last-cheque'
    && index === group.cheques.length - 1
    && !group.manualLast
}

export function chequeAmount(group, index, target) {
  if (isLastLocked(group, index)) return chequeRemainder(group, target)
  return Number(group.cheques[index]?.amount || 0)
}

export function chequeGroupTotal(group, target) {
  return group.cheques.reduce((sum, c, i) => sum + chequeAmount(group, i, target), 0)
}

const PRESET_KINDS = ['government', 'seller', 'agent', 'bank']

function normalizeCheque(cheque) {
  return {
    name: cheque?.name || '',
    remark: cheque?.remark || '',
    amount: cheque?.amount !== null && cheque?.amount !== undefined ? Number(cheque.amount) : null,
  }
}

// Merges a saved (possibly stale or partial) cheque-groups payload with the current
// preset shape, so a row saved before a new preset kind existed — or before a field
// like `remark` existed — still loads without losing whatever data it does have.
export function normalizeChequeGroups(savedGroups) {
  const defaults = createDefaultChequeGroups()
  if (!Array.isArray(savedGroups) || !savedGroups.length) return defaults

  const byKind = new Map(savedGroups.filter(g => g && g.kind).map(g => [g.kind, g]))

  const merged = defaults.map(def => {
    const saved = byKind.get(def.kind)
    if (!saved) return def
    byKind.delete(def.kind)
    return {
      id: saved.id || def.id,
      kind: def.kind,
      title: saved.title || def.title,
      manualLast: Boolean(saved.manualLast),
      cheques: Array.isArray(saved.cheques) && saved.cheques.length
        ? saved.cheques.map(normalizeCheque)
        : def.cheques,
    }
  })

  // Anything left over is a custom group (or an unrecognized kind, treated as custom).
  byKind.forEach(saved => {
    merged.push({
      id: saved.id || `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      kind: PRESET_KINDS.includes(saved.kind) ? saved.kind : 'custom',
      title: saved.title || '',
      manualLast: Boolean(saved.manualLast),
      cheques: Array.isArray(saved.cheques) ? saved.cheques.map(normalizeCheque) : [],
    })
  })

  return merged
}
